/**
 * PTScriptPick — whose reading of a photo to keep when the visitor chose
 * "detect automatically".
 *
 * The script of a photo is unknown until it is read, and every script has its
 * own recogniser. Until 2026-09-16 the page read with the Latin one and asked
 * a language detector about the result. Measured on 22 ground-truth images
 * (tests/ocr/auto-script-bench.mjs): mean CER 65.6 %, the wrong script on 15.
 * A Latin recogniser on Cyrillic returns almost nothing (4 characters of a
 * 187-character paragraph) or confident look-alikes (АПТЕКА → ANTEKA); the
 * detector then sees Latin, and the re-read never happens. On Chinese, Korean
 * and Arabic it returns digits or nothing — "text not found".
 *
 * What replaced it, by measurement, not by taste:
 *
 *  1. ONE detector pass, TWO recognisers — Latin and Cyrillic — on the same
 *     crops (ppocr.js `alsoLang`). The Cyrillic recogniser reads English and
 *     German letter for letter, but its dictionary lacks ß œ ł ś ż ą ę ć ń š ž
 *     č ř ş ğ ı, so it cannot simply replace the Latin one: Turkish, Polish and
 *     Czech signs would lose their diacritics.
 *  2. The choice is made PER LINE: the Cyrillic reading where it actually
 *     contains Cyrillic, the Latin reading otherwise. A Russian sign with
 *     "Wi-Fi" on it keeps both. Once the photo as a whole reads as Cyrillic,
 *     the Cyrillic reading wins on its other lines too — it knows ₽, which the
 *     Latin one writes as "10 000 P" (measured on a real contract photo: 3.1 %
 *     CER → 1.6 %) — unless the Latin reading carries a letter the Cyrillic
 *     dictionary does not have.
 *  3. When the photo as a whole reads as Cyrillic, words built only from
 *     letters that are IDENTICAL in both alphabets are folded to Cyrillic
 *     (KACCA → КАССА). Both recognisers write such words in Latin; only the
 *     rest of the photo can say which they are. "HOTEL" stays Latin: L has no
 *     identical twin.
 *  4. When neither recogniser read anything convincing, the text is in another
 *     script: Chinese, Arabic and Korean are tried in that order, and one is
 *     accepted only on evidence in its OWN script (CJK, Arabic letters, Hangul).
 *     Latin letters are never evidence — every dictionary carries them, and on
 *     a Russian sign the Korean recogniser produced MORE characters than the
 *     Cyrillic one, all of them Latin.
 *
 * Known limit, not a bug: a photo whose only word is made of shared letters
 * (МЕТРО) reads as Latin METPO — nothing in the picture says otherwise.
 */
(function (root) {
    'use strict';

    var CYR = /[\u0400-\u04FF]/g;
    var LETTER = /\p{L}/gu;
    var OWN = {
        eslav: /[\u0400-\u04FF]/g,
        ch: /[\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]/g,
        korean: /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]/g,
        arabic: /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/g,
    };

    /**
     * Below this mean confidence of the BETTER recogniser, the photo is not
     * treated as Latin or Cyrillic at all. Measured: correct Latin/Cyrillic
     * readings ≥ 0.72 on the corpus, the Arabic sign read by both 0.43 / 0.58.
     */
    var WEAK_CONF = 0.62;

    /** Other scripts to try, most likely first for this audience. */
    var FOREIGN = ['zh', 'ar', 'ko'];

    /** Letters the Cyrillic recogniser's dictionary lacks (measured on dict-eslav.txt). */
    var LATIN_ONLY = /[ßœŒłŁśŚźŹżŻąĄęĘćĆńŃšŠžŽčČřŘďĎťŤůŮőŐűŰğĞşŞıİ]/;

    /** Only letters that are drawn identically; see (3) above. */
    var STRICT = {
        A: 'А', B: 'В', C: 'С', E: 'Е', H: 'Н', K: 'К', M: 'М', O: 'О', P: 'Р',
        T: 'Т', X: 'Х', Y: 'У',
        a: 'а', c: 'с', e: 'е', o: 'о', p: 'р', x: 'х', y: 'у',
    };

    function count(s, re) {
        var m = String(s == null ? '' : s).match(re);
        return m ? m.length : 0;
    }
    function hasText(s) { return String(s == null ? '' : s).trim() !== ''; }
    function mean(a) {
        if (!a.length) return 0;
        var t = 0;
        for (var i = 0; i < a.length; i++) t += a[i];
        return t / a.length;
    }

    function foldStrict(text) {
        return String(text == null ? '' : text).split(/(\s+)/).map(function (tok) {
            var letters = tok.match(LETTER);
            if (!letters) return tok;
            for (var i = 0; i < letters.length; i++) if (!STRICT[letters[i]]) return tok;
            return Array.from(tok).map(function (ch) { return STRICT[ch] || ch; }).join('');
        }).join('');
    }

    /** One line of a dual engine → the reading to keep. `alt` is the Cyrillic one. */
    function pickLine(line) {
        var alt = line && line.alt;
        var at = alt ? String(alt.text || '') : '';
        var cyr = count(at, CYR);
        if (alt && cyr >= 2 && cyr >= 0.5 * count(at, LETTER)) {
            return { text: at, conf: alt.conf, words: alt.words || null, from: 'alt' };
        }
        if (hasText(line && line.text)) {
            return { text: line.text, conf: line.conf, words: line.words || null, from: 'main' };
        }
        return { text: at, conf: alt ? alt.conf : 0, words: alt ? alt.words || null : null, from: 'alt' };
    }

    /**
     * @param lines  ppocr.js lines of a dual engine (each may carry `alt`)
     * @return {{lines:Array, cyrillic:boolean, weak:boolean, cyr:number, letters:number}}
     *         `lines` are copies in reading order, `alt` removed, ready for PTCompose.
     */
    function mergeDual(lines) {
        var picks = [], confMain = [], confAlt = [];
        for (var i = 0; i < (lines ? lines.length : 0); i++) {
            var l = lines[i];
            if (hasText(l.text)) confMain.push(l.conf || 0);
            if (l.alt && hasText(l.alt.text)) confAlt.push(l.alt.conf || 0);
            picks.push({ line: l, pick: pickLine(l) });
        }
        var all = picks.map(function (x) { return x.pick.text; }).join('\n');
        var cyr = count(all, CYR);
        var letters = count(all, LETTER);
        var cyrillic = cyr >= 3 && cyr >= 0.2 * letters;

        var out = [];
        picks.forEach(function (x) {
            var l = x.line, p = x.pick;
            if (cyrillic && p.from === 'main' && l.alt && hasText(l.alt.text) && !LATIN_ONLY.test(String(l.text || ''))) {
                p = { text: l.alt.text, conf: l.alt.conf, words: l.alt.words || null, from: 'alt' };
            }
            if (!hasText(p.text)) return;
            var copy = {};
            for (var k in l) if (Object.prototype.hasOwnProperty.call(l, k) && k !== 'alt') copy[k] = l[k];
            copy.text = p.text;
            copy.conf = p.conf;
            copy.words = p.words;
            copy.from = p.from;
            out.push(copy);
        });
        if (cyrillic) {
            out.forEach(function (x) {
                x.text = foldStrict(x.text);
                if (x.words) x.words = x.words.map(function (w) {
                    var c = {};
                    for (var k in w) if (Object.prototype.hasOwnProperty.call(w, k)) c[k] = w[k];
                    c.text = foldStrict(w.text);
                    return c;
                });
            });
        }
        return {
            lines: out,
            cyrillic: cyrillic,
            weak: letters === 0 || Math.max(mean(confMain), mean(confAlt)) < WEAK_CONF,
            cyr: cyr,
            letters: letters,
        };
    }

    /** Did a single-script reading find text in ITS OWN script? */
    function foreignEvidence(lines, script) {
        var re = OWN[script];
        var all = (lines || []).map(function (x) { return x.text; }).join('\n');
        var own = re ? count(all, re) : 0;
        var letters = count(all, LETTER);
        return { own: own, letters: letters, ok: own >= 2 && own >= 0.5 * letters };
    }

    root.PTScriptPick = {
        mergeDual: mergeDual,
        pickLine: pickLine,
        foldStrict: foldStrict,
        foreignEvidence: foreignEvidence,
        WEAK_CONF: WEAK_CONF,
        FOREIGN: FOREIGN,
    };
})(typeof window !== 'undefined' ? window : globalThis);
